import { useMutation } from "@tanstack/react-query"

import {
  getTransportOperationName,
  getTransportOperationVersion,
  type TransportMutationVariables,
  type TransportOperationInput,
  type TransportOperationName,
  type TransportOperationVersionNumber,
} from "@/types_api/backend/transport/operations"

import { TransportContextNotReadyError } from "../utils/transportClient"
import { reportTransportError } from "../utils/transportErrorReporting"
import {
  executeTransportOperation,
  isTransportOperationReady,
  transportOperations,
} from "./transportOperationRuntime"
import type {
  TransportMutationOptions,
  TransportMutationOptionsWithOptionalVersion,
  TransportMutationResult,
  TransportVersionedMutationOptions,
  TransportVersionedOperationGroup,
  TransportVersionedOperationName,
} from "./transportQueryTypes"
import { useTransportClient } from "./useTransportClient"

export function useTransportMutation<
  Name extends TransportOperationName,
  TContext = unknown,
>(
  operation: TransportOperationInput<Name>,
  options?: TransportMutationOptions<Name, TContext>,
): TransportMutationResult<Name, TContext>

export function useTransportMutation<
  Group extends TransportVersionedOperationGroup,
  Version extends TransportOperationVersionNumber<Group>,
  Name extends TransportVersionedOperationName<Group, Version>,
  TContext = unknown,
>(
  operation: TransportOperationInput<Group>,
  options: TransportVersionedMutationOptions<Group, Version, TContext>,
): TransportMutationResult<Name, TContext>

export function useTransportMutation<
  Name extends TransportOperationName,
  TContext = unknown,
>(
  operation: TransportOperationInput<Name>,
  options?: TransportMutationOptionsWithOptionalVersion<Name, TContext>,
): TransportMutationResult<Name, TContext> {
  const baseOperationName = getTransportOperationName(operation)
  const operationName =
    options && "version" in options && options.version != null
      ? (getTransportOperationVersion(
          baseOperationName as TransportVersionedOperationGroup,
          options.version as TransportOperationVersionNumber<TransportVersionedOperationGroup>,
        ).name as Name)
      : baseOperationName
  const {
    onError,
    reportErrors = true,
    version: _version,
    ...mutationOptions
  } = options ?? {}
  const { getClient, isAuthLoaded, isOrganizationSynced, organizationSlug } =
    useTransportClient()
  const authRequired = transportOperations[operationName].auth === "session"
  const organizationRequired =
    transportOperations[operationName].organizationRequired
  const isReady = isTransportOperationReady({
    authRequired,
    isAuthLoaded,
    isOrganizationSynced,
    organizationRequired,
    organizationSlug,
  })

  const mutation = useMutation({
    ...mutationOptions,
    mutationFn: (request: TransportMutationVariables<Name>) => {
      if (!isReady) {
        throw new TransportContextNotReadyError(operationName)
      }

      return executeTransportOperation(getClient(), operationName, request)
    },
    onError: (error, variables, onMutateResult, mutationContext) => {
      if (reportErrors) {
        reportTransportError({
          error,
          kind: "mutation",
          operation: operationName,
          organizationSlug,
        })
      }

      return onError?.(error, variables, onMutateResult, mutationContext)
    },
  })

  return {
    ...mutation,
    isReady,
  }
}
